import React, { useEffect, useState } from 'react';
import { analyzeAI } from '../composables/fetchAPI';
import { formatCryptoSymbolsInText } from '../composables/fetchCrypto';

// แสดง X:ETHUSD ในข้อความ AI เป็น ETH/USD (ไม่แตะ url ของข่าว)
const prettifyCryptoSymbols = (value: any, key?: string): any => {
  if (typeof value === 'string') return key === 'url' ? value : formatCryptoSymbolsInText(value);
  if (Array.isArray(value)) return value.map((v) => prettifyCryptoSymbols(v));
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, prettifyCryptoSymbols(v, k)]));
  }
  return value;
};

interface ArticleByAIProps {
  aiPayload: any; // The payload for analyzeAI, shape as required by the endpoint
}

const ArticleByAI: React.FC<ArticleByAIProps> = ({ aiPayload }) => {
  const [loading, setLoading] = useState(false);
  const [aiResult, setAiResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!aiPayload || !aiPayload.symbol) {
      console.error('Invalid aiPayload:', aiPayload);
      setError('ข้อมูลไม่สมบูรณ์');
      return;
    }

    setLoading(true);
    setError(null);

    analyzeAI(aiPayload)
      .then((res) => {
        // console.log('AI Result:', res); // Debugging log
        setAiResult(res);
      })
      .catch((e) => {
        console.error('Error fetching AI analysis:', e);
        setError(e.message || 'เกิดข้อผิดพลาด');
      })
      .finally(() => setLoading(false));
  }, [aiPayload]);

  if (loading) return <div className="p-4 text-center text-yellow-500">กำลังวิเคราะห์ด้วย AI...</div>;
  if (error) return <div className="p-4 text-center text-red-500">{error}</div>;
  if (!aiResult) return null;

  const { disclaimer, result } = prettifyCryptoSymbols(aiResult);

  // Validate result structure
  const isValidString = (value: any) => typeof value === 'string';
  const isValidArray = (value: any) => Array.isArray(value);

  return (
  <div 
      className={`
        mx-auto rounded-lg shadow p-6 
        max-h-[600px] overflow-y-auto 
        md:max-h-none md:overflow-visible
      `}
    >
      {isValidString(result?.asOf) && (
        <div className="mb-4 text-xs text-end italic border-b pb-2" style={{ color: 'var(--secondary-text-color)' }}>
          AI วิเคราะห์เมื่อ: {new Date(result.asOf).toLocaleString()}
        </div>
      )}
      {isValidString(disclaimer) && (
        <div className="mb-4 text-xs italic border-b pb-2" style={{ color: 'var(--secondary-text-color)' }}>
          {disclaimer}
        </div>
      )}
      {result?.technicalOutlook && isValidString(result?.technicalOutlook.heading) && (
        <section className="mb-4">
          <h2 className="font-bold text-lg mb-1" style={{ color: 'var(--text-color)' }}>
            {result.technicalOutlook.heading}
          </h2>
          <p style={{ color: 'var(--text-color)' }}>
            {isValidString(result.technicalOutlook.content) ? result.technicalOutlook.content : 'ข้อมูลไม่ถูกต้อง'}
          </p>
        </section>
      )}
      {result?.newsFactors && isValidString(result?.newsFactors.heading) && (
        <section className="mb-4">
          <h2 className="font-bold text-lg mb-1" style={{ color: 'var(--text-color)' }}>
            {result.newsFactors.heading}
          </h2>
          <ul className="list-disc pl-6" style={{ color: 'var(--text-color)' }}>
            {isValidArray(result.newsFactors.items)
              ? result.newsFactors.items.map((item: string, idx: number) => (
                  <li key={idx} style={{ color: 'var(--text-color)' }}>
                    {item}
                  </li>
                ))
              : <li>ข้อมูลไม่ถูกต้อง</li>}
          </ul>
        </section>
      )}
      {result?.keyTakeaway && (
        <section className="mb-4">
          <h2 className="font-bold text-lg mb-1" style={{ color: 'var(--text-color)' }}>
            {isValidString(result.keyTakeaway.heading) ? result.keyTakeaway.heading : 'ข้อมูลไม่ถูกต้อง'}
          </h2>
          <div className="text-yellow-700 mb-1">
            ข้อควรระวัง: {isValidString(result.keyTakeaway.caution) ? result.keyTakeaway.caution : 'ข้อมูลไม่ถูกต้อง'}
          </div>
          <div className="text-green-700">
            โอกาส: {isValidString(result.keyTakeaway.opportunity) ? result.keyTakeaway.opportunity : 'ข้อมูลไม่ถูกต้อง'}
          </div>
        </section>
      )}
      {result?.recommendedActions && isValidArray(result.recommendedActions) && (
        <section className="mb-4">
          <h2 className="font-bold text-lg mb-1">คำแนะนำ</h2>
          {result.recommendedActions.map((action: any, idx: number) => (
            <div key={idx} className="mb-2 p-2 rounded">
              <span className="font-semibold">{isValidString(action?.label) ? action.label : 'ข้อมูลไม่ถูกต้อง'}</span>:{' '}
              {isValidString(action?.rationale) ? action.rationale : 'ข้อมูลไม่ถูกต้อง'}
              {isValidArray(action?.conditions) && (
                <ul className="list-disc pl-6 text-xs mt-1" style={{ color: 'var(--text-color)' }}>
                  {action.conditions.map((cond: string, i: number) => (
                    <li key={i}>{cond}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </section>
      )}
      {result?.risks && isValidArray(result.risks) && (
        <section className="mb-4">
          <h2 className="font-bold text-lg mb-1">ความเสี่ยง</h2>
          <ul className="list-disc pl-6" style={{ color: 'var(--text-color)' }}>
            {result.risks.map((risk: string, idx: number) => (
              <li key={idx}>{risk}</li>
            ))}
          </ul>
        </section>
      )}
      {isValidString(result?.summary) && (
        <section className="mb-4">
          <h2 className="font-bold text-lg mb-1">สรุป</h2>
          <p style={{ color: 'var(--text-color)' }}>{result.summary}</p>
        </section>
      )}
      {result?.newsContext && isValidArray(result.newsContext) && (
        <section className="mb-4">
          <h2 className="font-bold text-lg mb-1">ข่าวประกอบ</h2>
          <ul className="list-disc pl-6">
            {result.newsContext.map((news: any, idx: number) => (
              <li key={idx}>
                <a href={news?.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                  {isValidString(news?.title) ? news.title : 'ข้อมูลไม่ถูกต้อง'}
                </a>
                <span className="text-xs ml-2" style={{ color: 'var(--text-color)' }}>
                  ({isValidString(news?.source) ? news.source : 'ไม่ทราบแหล่งที่มา'})
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

export default ArticleByAI;